import { defineMessages } from 'react-intl';

export const formFillDemoMessages = defineMessages({
  sectionHeading: {
    defaultMessage: 'Try this form',
    id: '93EGCwJ+Um',
    description:
      'Section heading above the public form-fill demo on the community details page',
  },
  sectionDescription: {
    defaultMessage:
      'Paste a consultation transcript and Heidi will auto-fill the form fields above. No sign-up required.',
    id: 'a26tD76yd8',
    description: 'Sub-copy explaining what the form-fill demo does',
  },
  transcriptLabel: {
    defaultMessage: 'Consultation transcript',
    id: 'Qg1n+iHcly',
    description: 'Label for the transcript textarea in the form-fill demo',
  },
  transcriptPlaceholder: {
    defaultMessage: 'Paste, upload or use the sample transcript',
    id: 'ds1HdCKwWt',
    description: 'Placeholder copy for the transcript textarea',
  },
  trySampleButton: {
    defaultMessage: 'Use sample',
    id: 'pz5K2ZoMNp',
    description:
      'Button label that loads a specialty-matched sample transcript into the textarea',
  },
  uploadFileButton: {
    defaultMessage: 'Upload file',
    id: 'WMCT7IU8B6',
    description: 'Button label to upload either audio or document file for the demo',
  },
  fillFormButton: {
    defaultMessage: 'Fill form',
    id: 'S/rRYF74wj',
    description: 'Primary CTA that submits the transcript and triggers form auto-fill',
  },
  filling: {
    defaultMessage: 'Filling your form…',
    id: 'hlRdG+Yw0a',
    description: 'Status copy displayed while the LLM fills the form (5-15s)',
  },
  tryAnotherButton: {
    defaultMessage: 'Try another',
    id: 'MwPJ0bW4t2',
    description:
      'Reset button that returns the demo to the idle state with a fresh empty textarea',
  },
  signUpToSaveFilledFormButton: {
    defaultMessage: 'Sign up free',
    id: 'ILWee2Mouj',
    description: 'CTA button shown after successful form fill for logged-out users',
  },
  errorGeneric: {
    defaultMessage: 'Something went wrong while filling the form. Please try again.',
    id: 'sOPfpXfFoI',
    description: 'Generic error banner copy when the form-fill mutation fails',
  },
  errorRateLimited: {
    defaultMessage: 'Too many demos in a short time. Please wait a moment and try again.',
    id: 'rqke13/Jba',
    description: 'Specific error banner copy when the demo is rate-limited (429)',
  },
  filledStructuredFormReadyHeading: {
    defaultMessage: 'Form filled. Your filled form is now displayed.',
    id: 'jSJQUa595h',
    description:
      'Success state heading for an XML or JSON form, which renders as a field list rather than a PDF',
  },
  filledPdfReadyHeading: {
    defaultMessage: 'Form filled. Your filled PDF is now displayed.',
    id: '35JYB9uTYV',
    description:
      'Success state heading shown after the filled PDF replaces the empty preview',
  },
  filledPdfReadyDescription: {
    defaultMessage: 'Use "Try another" to reset and test a different input.',
    id: 'UDKclaqED0',
    description:
      'Success state helper text guiding the user to reset the demo and try another transcript',
  },
  charCount: {
    defaultMessage: '{count, number} / {max, number} characters',
    id: 'fbKbb7joE5',
    description: 'Character counter beneath the transcript textarea',
  },
  sectionDescriptionWithUploads: {
    defaultMessage: 'Add a transcript to see how this form is automatically filled.',
    id: 'L6IzeWnL1E',
    description:
      'Sub-copy for the form-fill demo when audio and document uploads are available',
  },
  tabText: {
    defaultMessage: 'Type or paste',
    id: 'DHypajT3Yz',
    description: 'Tab label for typing/pasting a transcript',
  },
  tabAudio: {
    defaultMessage: 'Upload audio',
    id: 'MagQhuzu9s',
    description: 'Tab label for uploading an audio recording',
  },
  tabDocument: {
    defaultMessage: 'Upload document',
    id: 'oxoJERHC+r',
    description: 'Tab label for uploading a document (PDF/Word/text)',
  },
  audioDropzoneTitle: {
    defaultMessage: 'Drag & drop an audio file, or click to browse',
    id: '0kxwVpwvDP',
    description: 'Primary instruction inside the audio upload dropzone',
  },
  documentDropzoneTitle: {
    defaultMessage: 'Drag & drop a document, or click to browse',
    id: 'R6+vIQm3oO',
    description: 'Primary instruction inside the document upload dropzone',
  },
  dropzoneAriaLabel: {
    defaultMessage: 'Upload file',
    id: 'bPou7k36EF',
    description: 'Accessible label for the upload dropzone button',
  },
  audioHelperText: {
    defaultMessage: 'MP3, WAV, M4A, WebM or OGG up to 10 MB',
    id: 'ZPCsKJl2Qi',
    description: 'Helper copy describing accepted audio formats and limits',
  },
  documentHelperText: {
    defaultMessage: 'PDF, Word, or plain-text file up to 10 MB',
    id: '/DbnzrsGb2',
    description: 'Helper copy describing accepted document formats and limits',
  },
  processingAudio: {
    defaultMessage: 'Transcribing your audio…',
    id: 'kVG5O6ktT4',
    description: 'Status copy while the audio is being transcribed',
  },
  processingDocument: {
    defaultMessage: 'Reading your document…',
    id: '+dS1PnSKqF',
    description: 'Status copy while the document is being extracted',
  },
  uploadLockHeading: {
    defaultMessage: 'Sign up to test uploads',
    id: 'zc2Mf7QDMv',
    description: 'Heading shown when upload demo inputs are locked for logged-out users',
  },
  uploadLockDescription: {
    defaultMessage: 'Create a free account to upload audio and documents in this demo.',
    id: 'b4g/h3pVdV',
    description:
      'Helper copy in locked upload overlay prompting users to sign up before testing uploads',
  },
  uploadLockSignupButton: {
    defaultMessage: 'Sign up free',
    id: 'Qzffyas448',
    description: 'Button label in locked upload overlay',
  },
  uploadSuccessAudio: {
    defaultMessage:
      'Audio file selected. Click "Fill form" to transcribe and fill this form.',
    id: 'D5UaIy7eiS',
    description: 'Success copy after an audio file is selected and queued for fill',
  },
  uploadSuccessDocument: {
    defaultMessage:
      'Document selected. Click "Fill form" to extract text and fill this form.',
    id: '5pVE4cnEr/',
    description: 'Success copy after a document is selected and queued for fill',
  },
  uploadSuccessHeading: {
    defaultMessage: 'File ready',
    id: 'RGHhmDSTjy',
    description: 'Heading shown in success banner after audio or document file selection',
  },
  selectedFileLabel: {
    defaultMessage: 'Selected file',
    id: '8K2QzIoESu',
    description: 'Label shown above selected file metadata in upload tabs',
  },
  removeFileButton: {
    defaultMessage: 'Remove',
    id: '6q6rS+sS4R',
    description: 'Action to remove the selected upload file',
  },
  errorHeading: {
    defaultMessage: 'Unable to fill this form',
    id: 'KzNaHcOGfS',
    description: 'Heading shown in error banner when form fill demo cannot proceed',
  },
  errorAudioInvalid: {
    defaultMessage:
      'Unsupported audio file. Please upload an MP3, WAV, M4A, WebM, or OGG file under 10 MB.',
    id: 'aRzEjROOtb',
    description: 'Error copy for an unsupported or oversize audio file',
  },
  errorDocumentInvalid: {
    defaultMessage:
      'Unsupported document. Please upload a single PDF, Word, or text file under 10 MB.',
    id: 'YZUqrOH5/w',
    description: 'Error copy for an unsupported or oversize document file',
  },
  errorAudioFailed: {
    defaultMessage: 'Could not transcribe that audio. Please try a different file.',
    id: 'Vn4TuLRADo',
    description: 'Generic error copy when audio transcription fails',
  },
  errorDocumentFailed: {
    defaultMessage: 'Could not read that document. Please try a different file.',
    id: 'YF94K9/8+b',
    description: 'Generic error copy when document extraction fails',
  },
});
